import Modal from "@/components/Modal/Modal";
import { MODAL_ID } from "@/lib/constants";
import formRenderer from "@/lib/formRenderer";
import { setModalOpen } from "@/store/slices/globalSlice";
import styles from "./UpdatePassword.module.scss";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { getInitialValues, requiredMessage } from "@/lib/utils";
import { Button } from "@mui/material";
import { useAppSelector } from "@/store/hooks";
import AuthService from "@/services/api/auth";
import toast from "@/lib/toast";
import { newPasswordRegex } from "@/lib/regex";

interface Props {
  title: string;
}

const UpdatePassword = ({ title }: Props) => {
  const { modalOpen } = useAppSelector((state) => state.global);
  const dispatch = useDispatch();

  const { register, control, handleSubmit, formState, reset, watch } = useForm({
    mode: "onChange",
  });

  const newPasswordValue = watch("newPassword");

  const formConfig = useMemo(
    () => ({
      currentPassword: {
        defaultValue: "",
        validations: {
          required: requiredMessage("Current password"),
        },
        controlType: "password",
        label: "Current Password",
        isRequired: true,
        isControlled: true,
        props: {
          type: "password",
          placeholder: "Enter your current password",
        },
      },
      newPassword: {
        defaultValue: "",
        validations: {
          required: requiredMessage("New password"),
          minLength: {
            value: 8,
            message: "New password must be at least 8 characters",
          },
          maxLength: {
            value: 20,
            message: "New password should be under 20 characters.",
          },
          // pattern: {
          //   value: newPasswordRegex,
          //   message:
          //     "Password must be at least 8 characters long, include 1 uppercase, 1 lowercase, 1 number, and 1 symbol",
          // },
        },
        controlType: "password",
        label: "New Password",
        isRequired: true,
        isControlled: true,
        props: {
          type: "password",
          placeholder: "Enter new password",
        },
      },
      confirmNewPassword: {
        defaultValue: "",
        validations: {
          required: requiredMessage("Confirm new password"),
          // matchField: {
          //   field: "newPassword",
          //   message: "Passwords do not match",
          // },
          validate: (value: any) =>
            value === newPasswordValue || "Confirm password does not match",
        },
        controlType: "password",
        label: "Confirm New Password",
        isRequired: true,
        isControlled: true,
        props: {
          type: "password",
          placeholder: "Re-enter new password",
        },
      },
    }),
    [modalOpen?.data, newPasswordValue]
  );

  const closeModal = () => {
    dispatch(setModalOpen({ id: null, data: null }));
    reset();
  };

  const onSubmit = async (values: any) => {
    try {
      const payload = {
        oldPassword: values?.currentPassword.trim(),
        newPassword: values?.confirmNewPassword.trim(),
      };

      const res = await AuthService.forgotPassword(payload);

      if (res?.success) {
        toast.success(res?.message || "Password changed successfully");
        closeModal();
      }
    } catch (error) {
      console.log("error", error);
    }
  };

  useEffect(() => {
    if (modalOpen) {
      reset(getInitialValues(formConfig));
    }
  }, [modalOpen]);

  return (
    <Modal
      heading={`${title}`}
      closeModal={closeModal}
      maxWidth="xs"
      modalOpen={modalOpen?.id === MODAL_ID?.CHANGE_PASSWORD}
    >
      <form
        onSubmit={handleSubmit(onSubmit)}
        className={styles.form}
        autoComplete="off"
      >
        <div className={styles.main}>
          {formRenderer(formConfig, formState, register, control)}
        </div>
        <div>
          <Button
            variant="contained"
            type="submit"
            fullWidth
            disabled={formState.isSubmitting || !formState.isValid}
          >
            Confirm
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default UpdatePassword;
